import type { NullableString } from '~/common/types/nullable.type.ts';

/** 인증 화면군 prefix — 이 아래 경로는 returnTo 대상이 될 수 없다(자기루프). */
const AUTH_PATH_PREFIX = '/auth/';
export const SIGN_IN_PATH = '/auth/sign-in';

/*
  토큰 저장·주입 헬퍼(getAccessToken / setAuthData / resetAuthData)는 제거했다.
  인증이 Supabase Auth 로 넘어가면서 세션 저장·갱신을 supabase-js 가 전담한다 —
  앱이 토큰을 직접 들고 있으면 두 곳의 만료 시점이 어긋나 "로그인된 것처럼 보이는데 요청은 401"
  같은 상태가 생긴다. 세션 판정은 `features/auth/api/auth.api.ts` 의 `getCurrentUser` 하나로 모은다.
*/

// ── returnTo 경로 조립 (auth 도메인 라우팅 규약의 정본) ──────────────────────
// 축출·이월 hop 이 여러 곳(라우트 loader · AuthGuard · 세션 만료 · 가입/로그인 링크)에 있어
// 인라인 재구현이 퍼지면 자기루프 가드·open-redirect 정규화가 곳마다 어긋난다. 조립은 여기서만 한다.

/**
 * returnTo 값을 내부 경로로만 허용(open-redirect 방어). 절대 URL·프로토콜 상대(`//`)는 폐기.
 * @description 값이 없거나 외부를 가리키면 홈(`/`)으로 접는다 — 호출부가 그대로 navigate 해도 안전.
 */
export function resolveReturnTo(returnTo: NullableString): string {
  if (returnTo && returnTo.startsWith('/') && !returnTo.startsWith('//')) {
    return returnTo;
  }
  return '/';
}

/**
 * 이월형 — 이미 손에 든 `returnTo` 를 다음 인증 경로로 넘긴다.
 * @description hop 마다 `resolveReturnTo` 정규화를 태워, 중간 경로에서 주입된 외부 URL 이
 *  다음 hop 으로 그대로 실려가지 않게 한다(홈으로 접힘). 이월할 값이 없으면 파라미터를 붙이지 않는다.
 * @param basePath 이동 대상 인증 경로 (`/auth/sign-in` · `/auth/sign-up`)
 * @param returnTo 이월할 원 목적지 (없으면 `null`)
 */
export function withReturnTo(basePath: string, returnTo: NullableString): string {
  if (!returnTo) return basePath;
  return `${basePath}?returnTo=${encodeURIComponent(resolveReturnTo(returnTo))}`;
}

/**
 * 축출형 — **현재 보고 있는 화면**을 returnTo 로 실은 로그인 경로.
 * @description 비로그인 진입(라우트 loader·AuthGuard)과 세션 만료 축출이 같은 형식을 쓰도록 강제한다
 *  (deep-verify H8 — 만료 경로만 returnTo 가 유실되던 비대칭).
 *  이미 인증 화면군에 있으면 자기 자신을 returnTo 로 실어 되돌아오는 루프를 만들지 않는다.
 */
export function buildSignInPathWithReturnTo(pathname: string, search: string): string {
  if (pathname.startsWith(AUTH_PATH_PREFIX)) return SIGN_IN_PATH;
  return withReturnTo(SIGN_IN_PATH, `${pathname}${search}`);
}
